import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import './TrialStatusBanner.css';

const TrialStatusBanner: React.FC = () => {
    const { user, isTrial, refreshUserProfile, refreshSearchStatus } = useAuth();
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState(true);

    // Refresh trial data every minute for trial users
    useEffect(() => {
        if (isTrial && user) {
            const interval = setInterval(() => {
                refreshUserProfile();
                refreshSearchStatus();
            }, 60000); // Every minute

            return () => {
                try {
                    if (typeof globalThis !== 'undefined' && typeof (globalThis as any).clearInterval === 'function') {
                        (globalThis as any).clearInterval(interval);
                    } else if (typeof clearInterval === 'function') {
                        clearInterval(interval as any);
                    }
                } catch {}
            };
        }
    }, [isTrial, user, refreshUserProfile, refreshSearchStatus]);

    // Don't show banner if user is not on trial or banner is dismissed
    if (!isTrial || !user || !isVisible) {
        return null;
    }

    const remainingDays = user.remainingTrialDays || 0;
    const searchLimits = user.searchLimits;
    const isExpired = user.isTrialExpired || false;

    const getDaysColor = (days: number) => {
        if (days <= 1) return 'critical';
        if (days <= 7) return 'warning';
        return 'normal';
    };

    const getSearchColor = (remaining: number, total: number) => {
        const percentage = (remaining / total) * 100;
        if (percentage <= 10) return 'critical';
        if (percentage <= 30) return 'warning';
        return 'normal';
    };

    const formatTimeUntilReset = (resetsAt: Date | null) => {
        if (!resetsAt) return '';
        
        const now = new Date();
        const reset = new Date(resetsAt);
        const diffMs = reset.getTime() - now.getTime();
        
        if (diffMs <= 0) return t('trial.searches.resetsNow');
        
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 0) {
            return t('trial.searches.resetsInHours', { hours, minutes });
        } else {
            return t('trial.searches.resetsInMinutes', { minutes });
        }
    };

    if (isExpired) {
        return (
            <div className="trial-banner trial-expired">
                <div className="trial-banner-content">
                    <span className="trial-icon">⏰</span>
                    <div className="trial-info">
                        <strong>{t('trial.expired.title')}</strong>
                        <span>{t('trial.expired.message')}</span>
                    </div>
                    <button 
                        className="trial-banner-dismiss"
                        onClick={() => setIsVisible(false)}
                        title={t('common.dismiss')}
                    >
                        ×
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="trial-banner trial-active">
            <div className="trial-banner-content">
                <span className="trial-icon">🚀</span>
                <div className="trial-info">
                    <div className="trial-days">
                        <strong className={`days-${getDaysColor(remainingDays)}`}>
                            {remainingDays === 1 ? 
                                t('trial.daysRemaining.singular', { count: remainingDays }) :
                                t('trial.daysRemaining.plural', { count: remainingDays })
                            }
                        </strong>
                        <span>{t('trial.message')}</span>
                    </div>
                    
                    {searchLimits && searchLimits.hasSearchLimit && (
                        <div className="trial-searches">
                            <span 
                                className={`searches-${getSearchColor(searchLimits.remainingSearches, searchLimits.dailyLimit)}`}
                            >
                                {t('trial.searches.remaining', {
                                    remaining: searchLimits.remainingSearches,
                                    total: searchLimits.dailyLimit
                                })}
                            </span>
                            {searchLimits.resetsAt && (
                                <span className="search-reset-time">
                                    {formatTimeUntilReset(searchLimits.resetsAt)}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                
                <div className="trial-actions">
                    <button 
                        className="trial-upgrade-btn"
                        onClick={() => {
                            // TODO: Implement upgrade to PRO flow
                            console.log('Upgrade to PRO clicked');
                        }}
                    >
                        {t('trial.upgrade')}
                    </button>
                    <button 
                        className="trial-banner-dismiss"
                        onClick={() => setIsVisible(false)}
                        title={t('common.dismiss')}
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TrialStatusBanner; 